import { z } from "zod";
import { dateSchema, localDate } from "../items/model";
const id = z.uuid(),
  time = z.iso.datetime();
const ids = z
  .array(id)
  .max(200)
  .refine((v) => new Set(v).size === v.length, "引用标识重复");
const base = {
  id,
  createdAt: time,
  updatedAt: time,
  revision: z.number().int().positive(),
  source: z.literal("user"),
  mode: z.literal("manual"),
  archivedAt: time.nullable(),
  deletedAt: time.nullable(),
  attachmentIds: z
    .array(id)
    .max(30)
    .refine((v) => new Set(v).size === v.length, "图片引用重复"),
};
export const studyPlanSchema = z
  .strictObject({
    ...base,
    title: z.string().trim().min(1).max(200),
    goal: z.string().max(20000),
    state: z.enum(["active", "paused", "completed"]),
    resourceIds: ids,
    taskIds: ids,
    nextTaskId: id.nullable(),
    endedAt: time.nullable(),
    result: z.string().max(20000),
    actionResolutions: z
      .array(
        z.strictObject({
          taskId: id,
          action: z.enum(["keep", "cancel", "unlink"]),
          resolvedAt: time,
        }),
      )
      .max(200),
  })
  .refine(
    (v) => !v.nextTaskId || v.taskIds.includes(v.nextTaskId),
    "下一行动须属于关联 Task",
  )
  .refine(
    (v) => (v.state === "completed") === (v.endedAt !== null),
    "计划结束时间与状态不一致",
  )
  .refine(
    (v) =>
      new Set(v.actionResolutions.map((r) => r.taskId)).size ===
        v.actionResolutions.length &&
      v.actionResolutions.every((r) => r.resolvedAt === v.endedAt),
    "结束处理事实不一致",
  );
export const studySessionSchema = z.strictObject({
  ...base,
  planId: id.nullable(),
  title: z.string().max(200),
  studyDate: dateSchema,
  durationMinutes: z.number().int().min(1).max(1440).nullable(),
  body: z.string().trim().min(1).max(200000),
  gained: z.string().max(200000),
  nextStep: z.string().max(20000),
  resourceIds: ids,
  nextTaskId: id.nullable(),
  state: z.enum(["draft", "recorded"]),
});
export const studyDraftSchemas = {
  "study-plan": z.strictObject({
    ...studyPlanSchema.shape,
    title: z.string().max(200),
  }),
  "study-session": studySessionSchema.extend({ body: z.string().max(200000) }),
};
export type StudyPlan = z.infer<typeof studyPlanSchema>;
export type StudySession = z.infer<typeof studySessionSchema>;
export type StudyItem = StudyPlan | StudySession;
export type StudyKind = "study-plan" | "study-session";
export const studySchemas = {
  "study-plan": studyPlanSchema,
  "study-session": studySessionSchema,
};
export const studyDraftSchema = z.strictObject({
  id,
  targetId: id,
  kind: z.enum(["study-plan", "study-session"]),
  baseRevision: z.number().int().nonnegative(),
  revision: z.number().int().positive(),
  createdAt: time,
  updatedAt: time,
  deletedAt: time.nullable(),
  payload: z.union([
    studyDraftSchemas["study-plan"],
    studyDraftSchemas["study-session"],
  ]),
});
export type StudyDraft = z.infer<typeof studyDraftSchema>;
export const studyRevisionSchema = z.strictObject({
  id: z.string().max(100),
  entityType: z.enum(["study-plan", "study-session"]),
  entityId: id,
  revision: z.number().int().positive(),
  reason: z.enum([
    "created",
    "edited",
    "paused",
    "resumed",
    "ended",
    "archived",
    "unarchived",
    "trashed",
    "restored",
    "history-restored",
  ]),
  recordedAt: time,
  snapshot: z.union([studyPlanSchema, studySessionSchema]),
});
export type StudyRevision = z.infer<typeof studyRevisionSchema>;
export const studyPath = (kind: StudyKind, id: string) =>
  `/study/${kind === "study-plan" ? "plans" : "records"}/${id}`;
export const studyTitle = (v: StudyItem) =>
  v.title ||
  ("body" in v ? v.body.split("\n")[0].slice(0, 80) : "未命名计划") ||
  "学习草稿";
export const planLabels = {
  active: "进行中",
  paused: "已暂停",
  completed: "已结束",
};
export function blankStudy(
  kind: "study-plan",
  zone?: string,
  key?: string,
): StudyPlan;
export function blankStudy(
  kind: "study-session",
  zone?: string,
  key?: string,
): StudySession;
export function blankStudy(
  kind: StudyKind,
  zone?: string,
  key?: string,
): StudyItem;
export function blankStudy(
  kind: StudyKind,
  zone = "UTC",
  key: string = crypto.randomUUID(),
): StudyItem {
  const now = new Date().toISOString(),
    common = {
      id: key,
      createdAt: now,
      updatedAt: now,
      revision: 1,
      source: "user" as const,
      mode: "manual" as const,
      archivedAt: null,
      deletedAt: null,
      attachmentIds: [],
      title: "",
      resourceIds: [],
      nextTaskId: null,
    };
  return kind === "study-plan"
    ? {
        ...common,
        state: "active",
        goal: "",
        taskIds: [],
        endedAt: null,
        result: "",
        actionResolutions: [],
      }
    : {
        ...common,
        state: "draft",
        planId: null,
        studyDate: localDate(new Date(), zone),
        durationMinutes: null,
        body: "",
        gained: "",
        nextStep: "",
      };
}
/** References stay canonical even in recycled objects, drafts and historical revisions. */
export function studyRefs(v: StudyItem) {
  return {
    resources: v.resourceIds,
    tasks:
      "taskIds" in v
        ? [...v.taskIds, ...v.actionResolutions.map((r) => r.taskId)]
        : v.nextTaskId
          ? [v.nextTaskId]
          : [],
    plan: "planId" in v ? v.planId : null,
  };
}
